import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { FaChartPie, FaBoxOpen } from "react-icons/fa";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, LineElement, PointElement, TimeScale } from "chart.js";
import { Bar, Pie, Line } from "react-chartjs-2";
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  TimeScale
);

function OrderReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateFrom: null,
    dateTo: null,
  });

  const vendorId = localStorage.getItem("vendorId");

  const fetchReport = async () => {
    try {
      setLoading(true);
      let url = `${baseurl}getVendorOrderReport/${vendorId}`;
      if (filters.dateFrom && filters.dateTo) {
        url += `?from_date=${filters.dateFrom}&to_date=${filters.dateTo}`;
      }
      const res = await axios.get(url);
      if (res.data.success) {
        setReport(res.data.report);
      } else {
        setReport(null);
      }
    } catch (err) {
      console.error("Error fetching vendor report:", err);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vendorId) fetchReport();
  }, [vendorId, filters]);

  if (loading) return <div className="text-center py-5">Loading report...</div>;
  if (!report) return <div className="text-center py-5 text-muted">No data available</div>;

  // Prepare charts data
  const statusData = {
    labels: Object.keys(report.orders_by_status),
    datasets: [{
      label: 'Orders by Status',
      data: Object.values(report.orders_by_status),
      backgroundColor: ['#facc15','#22c55e','#3b82f6','#f97316','#ef4444'],
      borderWidth: 1,
    }]
  };

  const paymentStatusData = {
    labels: Object.keys(report.orders_by_payment_status),
    datasets: [{
      label: 'Orders by Payment Status',
      data: Object.values(report.orders_by_payment_status),
      backgroundColor: ['#10b981', '#f59e0b'],
      borderWidth: 1,
    }]
  };

  const paymentMethodData = {
    labels: Object.keys(report.orders_by_payment_method),
    datasets: [{
      label: 'Orders by Payment Method',
      data: Object.values(report.orders_by_payment_method),
      backgroundColor: ['#3b82f6','#8b5cf6'],
      borderWidth: 1,
    }]
  };

  const ordersOverTimeData = {
    labels: Object.keys(report.orders_over_time),
    datasets: [{
      label: 'Orders Over Time',
      data: Object.values(report.orders_over_time),
      fill: true,
      backgroundColor: 'rgba(59,130,246,0.2)',
      borderColor: '#3b82f6',
      tension: 0.3
    }]
  };

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="main-content p-3">

          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <h4 className="breadcrumb-title d-flex align-items-center gap-2">
              <FaChartPie />   Order Report
            </h4>
            <div className="d-flex gap-2 flex-wrap">
              <Flatpickr
                placeholder="From"
                value={filters.dateFrom}
                onChange={date => setFilters({...filters, dateFrom: date[0]})}
                options={{ dateFormat: "Y-m-d" }}
                className="form-control"
              />
              <Flatpickr
                placeholder="To"
                value={filters.dateTo}
                onChange={date => setFilters({...filters, dateTo: date[0]})}
                options={{ dateFormat: "Y-m-d" }}
                className="form-control"
              />
              <button className="btn btn-primary px-4" onClick={fetchReport}>Apply</button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row mb-4 g-3">
            <div className="col-md-3 col-sm-6">
              <div className="card summary-card shadow-sm p-3 border-0">
                <h6>Total Orders</h6>
                <h3>{report.total_orders}</h3>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card summary-card shadow-sm p-3 border-0">
                <h6>Total Revenue</h6>
                <h3>₹{report.total_revenue}</h3>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card summary-card shadow-sm p-3 border-0">
                <h6>Average Order Value</h6>
                <h3>₹{report.average_order_value}</h3>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card summary-card shadow-sm p-3 border-0">
                <h6>Total Items Sold</h6>
                <h3>{report.total_items_sold}</h3>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="row mb-4 g-3">
            <div className="col-lg-4 col-md-6">
              <div className="card chart-card shadow-sm p-3 border-0">
                <h6>Orders by Status</h6>
                <Pie data={statusData} />
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="card chart-card shadow-sm p-3 border-0">
                <h6>Orders by Payment Status</h6>
                <Pie data={paymentStatusData} />
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="card chart-card shadow-sm p-3 border-0">
                <h6>Orders by Payment Method</h6>
                <Pie data={paymentMethodData} />
              </div>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-12">
              <div className="card chart-card shadow-sm p-3 border-0">
                <h6>Orders Over Time</h6>
                <Line data={ordersOverTimeData} />
              </div>
            </div>
          </div>

          {/* Top Selling Products */}
          <div className="card shadow-sm mb-4 border-0">
            <div className="card-body">
              <h6 className="mb-3">Top Selling Products</h6>
              <div className="row g-3">
                {report.top_selling_products.map((p, idx) => (
                  <div className="col-lg-3 col-md-4 col-sm-6" key={idx}>
                    <div className="card product-card shadow-sm h-100">
                      <img
                        src={p.image ? `${imgurl}${p.image}` : "/noimage.png"}
                        className="card-img-top"
                        alt={p.name}
                        style={{ height: "180px", objectFit: "cover" }}
                      />
                      <div className="card-body p-2 text-center">
                        <h6 className="mb-1">{p.name}</h6>
                        <small className="text-muted">Qty Sold: {p.quantity_sold}</small>
                        <div className="fw-semibold">₹{p.price}</div>
                        <div className="text-muted" style={{ fontSize: "0.8rem" }}>MRP: ₹{p.mrp}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </main>

      {/* Custom CSS */}
      <style jsx>{`
        .summary-card {
          background: linear-gradient(135deg, #e0f2fe, #bae6fd);
          border-radius: 12px;
          text-align: center;
          transition: transform 0.2s;
        }
        .summary-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 6px 15px rgba(0,0,0,0.15);
        }
        .chart-card {
          border-radius: 12px;
          transition: transform 0.2s;
        }
        .chart-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .product-card {
          border-radius: 12px;
          transition: transform 0.2s;
        }
        .product-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 6px 15px rgba(0,0,0,0.15);
        }
        .form-control {
          min-width: 120px;
        }
      `}</style>
    </>
  );
}

export default OrderReport;
